import React, { useCallback, useEffect, useRef, useState } from 'react';

import { getWorkflowRoute } from '@aline/common';
import { link, params } from '@aline/routes';
import type { SelectChangeEvent } from '@mui/material';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  Skeleton,
  Typography,
  useTheme,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import { capitalCase } from 'change-case';
import clsx from 'clsx';
import { Eye, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  AlineTooltip,
  AnimatedArrowIcon,
  ContractTypePicker,
  getFileFormat,
  getWorkflowRuntimeString,
  OCRRunningCell,
  PanelHeader,
  RelationshipTypeSelect,
  SelectRelatedDocumentDialog,
  Spacer,
  StorableIcon,
  UploadFilesPicker,
} from 'src/components';
import type { ContractTypeDocument, StorableIconDocument } from 'src/components';
import { AiReasoningTooltipButton } from 'src/components/ai/shared/components/AiReasoningTooltipButton';
import {
  getRelationshipLabel,
  SelectRelatedDocumentsWithRelationshipDialog,
} from 'src/components/generic/SelectRelatedDocumentsWithRelationshipDialog';
import { AddIcon } from 'src/components/icons/AddIcon';
import { CancelCircleIcon } from 'src/components/icons/CancelCircleIcon';
import { CheckCircleIcon } from 'src/components/icons/CheckCircleIcon';
import { ChildDocumentIcon } from 'src/components/icons/ChildDocumentIcon';
import { FavoriteDocumentIcon } from 'src/components/icons/FavoriteDocumentIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import { InfoIcon } from 'src/components/icons/InfoIcon';
import { MagnifyingGlassIcon } from 'src/components/icons/MagnifyingGlassIcon';
import { PlayWorkflowIcon } from 'src/components/icons/PlayWorkflowIcon';
import { PulseIcon, PulseIconVariant } from 'src/components/icons/Pulse';
import { SmallAiSparkleIcon } from 'src/components/icons/SmallAiSparkleIcon';
import { StopIcon } from 'src/components/icons/StopIcon';
import { UploadNewDocumentIcon } from 'src/components/icons/UploadNewDocumentIcon';
import { UrlLinkIcon } from 'src/components/icons/UrlLinkIcon';
import { WarningCircleIcon } from 'src/components/icons/WarningCircleIcon';
import { getDocumentStatusColor } from 'src/components/reports/report-detail/GridStatusCell';
import { useRelationshipRowTitleClick } from 'src/components/repository/repositoryDocumentNameCellHelpers';
import { RepositoryPartiesList } from 'src/components/repository/RepositoryPartiesList';
import {
  getIsRelationshipLookingForAMatch,
  getIsRelationshipMatchingRunning,
} from 'src/components/repository/useRelationshipNameCellState';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import {
  getWorkflowStatusChipColor,
  getWorkflowStatusChipTextColor,
} from 'src/components/workflow/workflow-list/cells/WorkflowStatusChip';
import {
  ExtractionQuestionCard,
  getIsCoreAiRunning,
  getIsExtractionJobRunning,
  getIsExtractionQuestionRunning,
  sortExtractionLocations,
  useScrollToLocationForReference,
} from 'src/editor/toolbars/right-panel/ai';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useDocumentStatus } from 'src/gql/hooks/document';
import {
  DateKey,
  Direction,
  domElIdCreators,
  doNothing,
  getDateComparator,
  getOrphanRelationshipDisplay,
  openLink,
  openLinkFromAnchorClick,
  useConsumePendingParam,
  useDispatchInfoSnack,
} from 'src/helpers';
import {
  useCancelExtractionJobs,
  useCreateExtractionQuestion,
  useGetDocumentForSummaryDisplay,
  useGetDocumentHeader,
  useGetExtractionQuestionsForDocument,
  useRunExtractionJobsForDocuments,
} from 'src/hooks';
import { useEditorLoading, useMaybeEditor } from 'src/layout-engine';
import { alineLinkButtonStylesWithUnderline, createClampStyles } from 'src/theme';
import type { ReactSetStateTuple } from 'src/types';
import { getWorkflowStateDisplayString } from 'src/workflow';
import { makeStyles } from 'tss-react/mui';
import { v4 as uuid4 } from 'uuid';

import {
  useArrivalHighlightedRelationshipId,
  useArrivalHighlightScroll,
  useArrivalHighlightStyles,
} from './use-arrival-highlight';
import type {
  PendingSuggestionItem,
  SummaryRelatedDocumentState,
} from './use-summary-related-document-state';
import {
  getAiBulkReviewPayload,
  useSummaryRelatedDocumentState,
} from './use-summary-related-document-state';
import {
  useConfirmRelationship,
  useRejectRelationship,
  useSetChildAndConfirm,
  useSetParentAndConfirm,
  useUpdateRelationshipType,
} from './useRelationshipMutations';
import type { RelationshipForMutation } from './useRelationshipMutations';
import { getOCRStatusFromFileVersion, OCRStatus } from '../ai/shared/OCRStatusOrChildren';
import { useBackToAI } from '../BackToAIButton';

/** Size of the storable icon in a related-document row. The caption spacer matches it. */
const RELATED_DOC_ICON_SIZE = 20;

const useStyles = makeStyles<void, 'relatedDocRowActions' | 'relatedDocRowTitleText'>()(
  (theme, _params, classes) => ({
    aiSuggestionActions: {
      alignItems: 'center',
      display: 'flex',
      flex: '0 0 auto',
      gap: theme.spacing(0.5),
    },
    aiSuggestionRow: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    aiSuggestionRowBottom: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      width: '100%',
    },
    aiSuggestionRowTop: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    aiSuggestionsGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(0.5),
      padding: theme.spacing(1.5),
      width: '100%',
    },
    aiSuggestionsGroupLabel: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 700,
      textTransform: 'uppercase',
    },
    aiSuggestionsHeaderActionButton: {
      color: 'rgba(55, 73, 81, 1)',
      textTransform: 'none',
    },
    aiSuggestionsHeaderActions: {
      display: 'flex',
      gap: theme.spacing(1),
    },
    aiSuggestionsHeaderLeft: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      minWidth: 0,
    },
    aiSuggestionsHeaderRow: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(0.5, 0),
      width: '100%',
    },
    aiSuggestionsHeaderText: {
      fontWeight: 600,
    },
    createdAt: {
      fontSize: 12,
      whiteSpace: 'nowrap',
    },
    divider: {
      margin: theme.spacing(1, 1.5, 0),
    },
    documentTitle: {
      ...alineLinkButtonStylesWithUnderline,
      fontSize: theme.typography.h1.fontSize,
      width: 'fit-content',
    },
    header: {
      width: '100%',
    },
    headerGap: {
      gap: theme.spacing(1),
    },
    headerSection: {
      backgroundColor: theme.appColors.background,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: theme.spacing(2),
    },
    internalReferenceCaption: {
      color: theme.palette.text.secondary,
      fontStyle: 'italic',
    },
    label: {
      fontWeight: 600,
      marginBottom: theme.spacing(1),
    },
    metaChip: {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      fontSize: 12,
      height: 20,
    },
    metaRow: {
      alignItems: 'center',
      color: theme.palette.text.secondary,
      display: 'flex',
      flexWrap: 'nowrap',
      gap: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    noQuestions: {
      alignItems: 'center',
      display: 'flex',
      height: 100,
      justifyContent: 'center',
    },
    orphanNameColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
      minWidth: 0,
    },
    orphanWarningText: {
      color: IconColors.WarnAmber,
      fontSize: 12,
      fontWeight: 600,
    },
    panelContainer: {
      backgroundColor: theme.palette.common.white,
      boxSizing: 'border-box',
      flexGrow: 1,
      overflowY: 'auto',
      width: '100%',
    },
    popover: {
      marginLeft: -30,
    },
    popoverPaper: {
      width: 400,
    },
    questionContainer: {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      boxSizing: 'border-box',
      marginBottom: theme.spacing(0.5),
      padding: theme.spacing(0.5),
    },
    relatedCountChip: {
      '& .MuiChip-label': {
        color: '#146EE8',
        fontFamily: '"Open Sans", sans-serif',
        fontSize: 14,
        fontWeight: 700,
        paddingLeft: 0,
        paddingRight: 0,
      },
      backgroundColor: '#C0D5F1',
      borderRadius: 4,
      height: 20,
      marginLeft: 16,
      minWidth: 20,
      width: 20,
    },
    relatedDocRow: {
      [`&:hover .${classes.relatedDocRowActions}`]: {
        opacity: 1,
      },
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
      justifyContent: 'space-between',
      padding: theme.spacing(0.5, 0),
      width: '100%',
    },
    relatedDocRowActions: {
      opacity: 0,
      transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shortest,
      }),
    },
    relatedDocRowDivider: {
      alignSelf: 'stretch',
      backgroundColor: theme.palette.divider,
      flexShrink: 0,
      // Vertical spacing so the line is visible between rows.
      marginBottom: theme.spacing(1),
      marginLeft: 0,
      marginTop: theme.spacing(1),
    },
    relatedDocRowIcon: {
      color: '#146EE8',
      flex: '0 0 auto',
    },
    relatedDocRowLeft: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      gap: theme.spacing(1),
      minWidth: 0,
    },
    relatedDocRowRelationship: {
      color: theme.palette.text.secondary,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: 'nowrap',
    },
    relatedDocRowTitle: {
      ...alineLinkButtonStylesWithUnderline,
      '& .MuiButton-startIcon': {
        marginLeft: 0,
        marginRight: theme.spacing(1),
      },
      '&.Mui-disabled': {
        opacity: 1,
      },
      [`&.Mui-disabled .${classes.relatedDocRowTitleText}`]: {
        color: '#146EE8',
      },
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      },
      flex: 1,
      fontSize: 14,
      justifyContent: 'flex-start',
      minWidth: 0,
      padding: 0,
      textAlign: 'left',
      textDecoration: 'none',
      textTransform: 'none',
      width: 0,
    },
    relatedDocRowTitleText: {
      display: 'block',
      minWidth: 0,
      overflow: 'hidden',
      wordBreak: 'break-word',
    },
    relatedDocsAddButton: {
      borderRadius: 48,
      borderWidth: 1,
      height: 24,
      minHeight: 24,
      padding: theme.spacing(1, 1.5),
      textTransform: 'none',
    },
    relatedDocsEmptyState: {
      alignItems: 'center',
      display: 'flex',
      flex: 1,
      flexDirection: 'column',
      gap: theme.spacing(2),
      justifyContent: 'center',
      textAlign: 'center',
    },
    relatedDocsEmptyText: {
      fontSize: 12,
      fontWeight: 600,
      textAlign: 'center',
    },
    relatedDocsGroup: {
      // Add spacing only between adjacent groups (e.g., Parent vs Child Documents)
      '& + &': {
        marginTop: theme.spacing(1),
      },
      backgroundColor: '#146EE80A',
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      minHeight: 152,
      padding: theme.spacing(1, 1.5),
      width: '100%',
    },
    relatedDocsGroupTitleRow: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    relatedDocsGroupTitleText: {
      fontWeight: 600,
      marginBottom: 0,
    },
    relatedDocsList: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
    },
    relationshipSelect: {
      '& .MuiSelect-select': {
        borderRadius: 6,
        cursor: 'pointer',
        paddingBottom: 0,
        paddingTop: 0,
        transition: theme.transitions.create('background-color', {
          duration: theme.transitions.duration.shortest,
        }),
      },
      '& .MuiSelect-select:hover': {
        backgroundColor: theme.palette.action.hover,
      },
      '&.Mui-disabled .MuiSelect-select:hover': {
        backgroundColor: 'transparent',
      },
    },
    relationshipsBanner: {
      alignItems: 'center',
      background: '#146EE833',
      borderRadius: 8,
      display: 'flex',
      gap: theme.spacing(1),
      margin: theme.spacing(2, 2, 0),
      padding: theme.spacing(1.5, 2),
    },
    relationshipsBannerTitle: {
      color: theme.palette.primary.main,
      fontSize: 14,
      fontWeight: 700,
    },
    root: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
    },
    runtimeChip: {
      backgroundColor: theme.palette.common.white,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: theme.shape.borderRadius,
      fontSize: 12,
      height: 20,
    },
    section: {
      backgroundColor: theme.palette.common.white,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1, 2),
    },
    sectionContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: 0,
    },
    sectionHeader: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'flex-start',
      padding: theme.spacing(1, 0),
      width: '100%',
    },
    selfReferencingCaptionRow: {
      display: 'flex',
      // Matches the title row's gap, so the spacer alone aligns the caption with the title.
      gap: theme.spacing(1),
    },
    selfReferencingCaptionSpacer: {
      flex: `0 0 ${RELATED_DOC_ICON_SIZE}px`,
    },
    statusChip: {
      '& .MuiChip-label': {
        padding: '0 8px',
      },
      borderRadius: theme.shape.borderRadius,
      fontSize: 12,
      height: 20,
    },
    titleLink: {
      color: 'inherit',
      textDecoration: 'none',
    },
    value: {
      display: 'flex',
      flexWrap: 'nowrap',
      gap: theme.spacing(1),
    },
    workflowSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    workflowStage: {
      color: theme.palette.text.secondary,
      fontSize: 12,
    },
    workflowStatusRow: {
      alignItems: 'center',
      display: 'flex',
      gap: theme.spacing(1),
    },
    workflowTitle: {
      ...alineLinkButtonStylesWithUnderline,
      fontSize: theme.typography.subtitle2.fontSize,
      width: 'fit-content',
    },
  })
);

interface DocumentSummaryDrawerProps {
  documentId: UUID;
}

export function DocumentSummaryDrawer({ documentId }: DocumentSummaryDrawerProps): JSX.Element {
  const { classes } = useStyles();
  const [relatedDocsExpanded, setRelatedDocsExpanded] = useState(true);
  const [reviewBannerDismissed, setReviewBannerDismissed] = useState(false);
  const [aiPendingSuggestionsForDocCount, setAiPendingSuggestionsForDocCount] = useState(0);
  const { document: alineDocument, loading } = useGetDocumentForSummaryDisplay(documentId);
  const isRelationshipMatchingRunning = getIsRelationshipMatchingRunning(alineDocument);
  const showRelationshipBanner =
    (aiPendingSuggestionsForDocCount > 0 || isRelationshipMatchingRunning) &&
    !reviewBannerDismissed;

  const handleBackToAI = useBackToAI();
  const handleBulkReviewComplete = useCallback(() => {
    setReviewBannerDismissed(true);
  }, []);
  const handleReviewDocuments = useCallback(() => {
    setRelatedDocsExpanded(true);
    const el = document.getElementById(
      domElIdCreators.documentSummaryRelatedDocumentsHeader(documentId)
    );
    requestAnimationFrame(() => {
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [documentId]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <PanelHeader
          showBackButton
          backButtonTooltip="Back to AI"
          text="Document Summary"
          onClickBack={handleBackToAI}
        />
      </div>
      <div className={classes.panelContainer}>
        {showRelationshipBanner && (
          <div className={classes.relationshipsBanner}>
            {isRelationshipMatchingRunning ? (
              <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
            ) : (
              <InfoIcon sx={{ fontSize: 24 }} />
            )}
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flex: 1,
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography className={classes.relationshipsBannerTitle}>
                  {isRelationshipMatchingRunning
                    ? 'Aline AI is detecting relationships.'
                    : `Aline AI found ${aiPendingSuggestionsForDocCount} relationship${aiPendingSuggestionsForDocCount === 1 ? '' : 's'}.`}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                }}
              >
                <AlineTooltip arrow placement="top" title="Dismiss">
                  <IconButton size="small" onClick={() => setReviewBannerDismissed(true)}>
                    <X size={18} />
                  </IconButton>
                </AlineTooltip>
                <AlineTooltip arrow placement="top" title="Review relationships">
                  <IconButton color="primary" size="small" onClick={handleReviewDocuments}>
                    <Eye size={18} />
                  </IconButton>
                </AlineTooltip>
              </Box>
            </Box>
          </div>
        )}
        <DocumentHeader documentId={documentId} />
        <FileDetails document={alineDocument} loading={loading} />
        <Divider className={classes.divider} />
        <RelatedDocuments
          document={alineDocument}
          documentId={documentId}
          expanded={relatedDocsExpanded}
          loading={loading}
          onAiPendingSuggestionsCountChange={setAiPendingSuggestionsForDocCount}
          onBulkReviewComplete={handleBulkReviewComplete}
          onExpandedChange={setRelatedDocsExpanded}
        />
        <Divider className={classes.divider} />
        <AIProperties documentId={documentId} />
      </div>
    </div>
  );
}

interface SectionProps {
  children: React.ReactNode;
  title: string;
  headerChildren?: React.ReactNode;
  headerId?: string;
}

function Section({ children, headerChildren, headerId, title }: SectionProps): JSX.Element {
  const { classes } = useStyles();

  return (
    <div className={classes.section}>
      <div className={classes.sectionHeader} id={headerId}>
        <Typography variant="h2">{title}</Typography>
        {headerChildren}
      </div>
      <div className={classes.sectionContent}>{children}</div>
    </div>
  );
}

interface DocumentHeaderProps {
  documentId: UUID;
}

function TruncatedTextWithTooltip({ text }: { text: string }): JSX.Element {
  return (
    <Typography component="span" sx={createClampStyles(2)} variant="inherit">
      {text}
    </Typography>
  );
}

function DocumentHeader({ documentId }: DocumentHeaderProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const documentStatus = useDocumentStatus(documentId);
  const { document, loading } = useGetDocumentHeader(documentId);

  if (loading || !document) {
    return (
      <div className={classes.headerSection}>
        <Skeleton height={40} variant="text" width={200} />
        <div className={classes.metaRow}>
          <Skeleton variant="text" width={120} />
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} height={20} variant="rectangular" width={60} />
          ))}
        </div>
      </div>
    );
  }

  const fileType = getDocumentFileType(document);
  const PERMISSION_LABELS: Record<Apollo.PermissionRole, string> = {
    [Apollo.PermissionRole.Editor]: 'Can edit',
    [Apollo.PermissionRole.Suggester]: 'Can suggest',
    [Apollo.PermissionRole.Commenter]: 'Can comment',
    [Apollo.PermissionRole.Viewer]: 'Can view',
  };
  const permissionLabel = document.requesterRole
    ? PERMISSION_LABELS[document.requesterRole]
    : 'No permissions';

  const documentHref = link.DocumentDetail({ documentId: document.id });
  const workflowHref = document.workflow
    ? getWorkflowRoute(document.workflow.id, {
        isTemplate: document.workflow.variant === Apollo.WorkflowVariant.Template,
      })
    : '';

  return (
    <div className={classes.headerSection}>
      <Link
        className={classes.titleLink}
        to={documentHref}
        onClick={e =>
          openLinkFromAnchorClick(e, documentHref, {
            title: document.name || 'Untitled Document',
          })
        }
      >
        <Typography className={classes.documentTitle} variant="h1">
          {document.name || 'Untitled'}
        </Typography>
      </Link>
      <div className={classes.metaRow}>
        <Typography className={classes.createdAt} variant="body2">
          Created at: {new Date(document.createdAt).toLocaleDateString()}
        </Typography>
        <Chip className={classes.metaChip} label={fileType} size="small" />
        <Chip
          className={classes.statusChip}
          label={capitalCase(documentStatus || 'Draft')}
          size="small"
          style={{
            backgroundColor: getDocumentStatusColor({ status: documentStatus }),
            color:
              documentStatus === Apollo.DocumentStatus.Draft
                ? theme.palette.text.primary // Dark text for better contrast on yellow.
                : theme.palette.common.white,
          }}
        />
        <Chip className={classes.metaChip} label={permissionLabel} size="small" />
      </div>
      {document?.workflow && (
        <div className={classes.workflowSection}>
          <Link
            className={classes.titleLink}
            to={workflowHref}
            onClick={event =>
              openLinkFromAnchorClick(event, workflowHref, {
                title: document.workflow?.name || 'Workflow',
              })
            }
          >
            <Typography className={classes.workflowTitle} variant="subtitle2">
              {document.workflow.name || 'Untitled'}
            </Typography>
          </Link>
          <Box className={classes.workflowStatusRow}>
            <Typography className={classes.workflowStage} variant="body2">
              Stage:
            </Typography>
            <Chip
              className={classes.statusChip}
              label={getWorkflowStateDisplayString(document.workflow.state)}
              size="small"
              style={{
                backgroundColor: getWorkflowStatusChipColor(document.workflow.status),
                color: getWorkflowStatusChipTextColor(document.workflow.status),
              }}
            />
            <Chip
              className={classes.runtimeChip}
              label={`Runtime: ${getWorkflowRuntimeString(document.workflow)}`}
              size="small"
            />
          </Box>
        </div>
      )}
    </div>
  );
}

type SummaryDocument = Apollo.GetDocumentFileDetailsQuery['document'] | null;

interface FileDetailsProps {
  document: SummaryDocument;
  loading: boolean;
}

export function getDocumentFileType(
  document: NonNullable<ReturnType<typeof useGetDocumentHeader>['document']>
): string {
  switch (document.latestVersion.__typename) {
    case 'DocumentVersion':
      return 'Aline Document';
    case 'StaticFileVersion': {
      const fileFormat = getFileFormat(document.latestVersion.fileLocation.mimetype);

      switch (fileFormat) {
        case 'default':
          return 'Document';
        default:
          return fileFormat.toUpperCase();
      }
    }
    default:
      return 'Document';
  }
}

function ContractTypeDisplay({
  document,
  isAiSettingType,
}: {
  document: ContractTypeDocument;
  isAiSettingType: boolean;
}) {
  const { classes } = useStyles();
  const ocrStatus = getOCRStatusFromFileVersion(document.latestVersion);

  if (ocrStatus === OCRStatus.Running) return <OCRRunningCell pulsePosition="left" />;
  if (isAiSettingType) {
    return (
      <div className={classes.value}>
        <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
        <Typography noWrap variant="body1">
          AI setting type...
        </Typography>
      </div>
    );
  }

  return <ContractTypePicker document={document} />;
}

function PartiesDisplay({
  document,
  isAiSettingParties,
}: {
  document: NonNullable<Apollo.GetDocumentFileDetailsQuery['document']>;
  isAiSettingParties: boolean;
}) {
  const { classes } = useStyles();
  const ocrStatus = getOCRStatusFromFileVersion(document.latestVersion);

  if (ocrStatus === OCRStatus.Running) return <OCRRunningCell pulsePosition="left" />;
  if (isAiSettingParties) {
    return (
      <div className={classes.value}>
        <PulseIcon variant={PulseIconVariant.LoadingPrimary} />
        <Typography noWrap variant="body1">
          AI setting parties...
        </Typography>
      </div>
    );
  }

  return <RepositoryPartiesList document={document} position="bottom" textColor="textPrimary" />;
}

function FileDetails({ document, loading }: FileDetailsProps): JSX.Element {
  const { classes } = useStyles();
  const isCoreAiRunning = getIsCoreAiRunning(document);

  if (loading || !document) {
    return (
      <Section title="File Details">
        <div>
          <Typography className={classes.label} variant="body2">
            File Owner
          </Typography>
          <Skeleton variant="text" width={120} />
        </div>
        <div>
          <Typography className={classes.label} variant="body2">
            Contract Type
          </Typography>
          <Skeleton variant="text" width={120} />
        </div>
        <div>
          <Typography className={classes.label} variant="body2">
            Parties
          </Typography>
          <Skeleton variant="text" width={120} />
        </div>
      </Section>
    );
  }

  return (
    <Section title="File Details">
      <div>
        <Typography className={classes.label} variant="body2">
          File Owner
        </Typography>
        <Typography className={classes.value} variant="body1">
          {document.owner.displayName}
        </Typography>
      </div>
      <div>
        <Typography className={classes.label} variant="body2">
          Contract Type
        </Typography>
        <div className={classes.value}>
          <ContractTypeDisplay document={document} isAiSettingType={isCoreAiRunning} />
        </div>
      </div>
      <div>
        <Typography className={classes.label} variant="body2">
          Parties
        </Typography>
        <div className={classes.value}>
          <PartiesDisplay document={document} isAiSettingParties={isCoreAiRunning} />
        </div>
      </div>
    </Section>
  );
}

type RelationshipDirection = 'parent' | 'child';

function OrphanRelationshipEditorRow({
  classes,
  documentId,
  orphanDirection,
  relationship,
  repositoryId,
  sourceDocumentName,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  documentId: UUID;
  orphanDirection: 'parent' | 'child';
  relationship: UnresolvedRelationship;
  repositoryId: UUID | null;
  sourceDocumentName: string | undefined;
}): JSX.Element {
  const orphanRelType = relationship.childRelationshipType;
  const { loading: rejecting, reject } = useRejectRelationship();
  const [resolutionDialog, setResolutionDialog] = useState<'select' | 'upload' | null>(null);
  const { loading: settingParent, setParentAndConfirm } = useSetParentAndConfirm();
  const { loading: settingChild, setChildAndConfirm } = useSetChildAndConfirm();
  const labelDirection = orphanDirection === 'parent' ? 'child' : 'parent';
  const updating = rejecting || settingParent || settingChild;
  /** `secondLineName` is the extracted name shown above the not-found text. */
  const { notFoundText, secondLineName } = getOrphanRelationshipDisplay({
    expectedDocumentName: relationship.expectedDocumentName,
    missingSide: orphanDirection,
  });

  async function handleResolve(selectedDocId: UUID): Promise<void> {
    switch (orphanDirection) {
      case 'parent':
        await setParentAndConfirm(relationship, selectedDocId);
        break;
      case 'child':
        await setChildAndConfirm(relationship, selectedDocId);
        break;
    }
    setResolutionDialog(null);
  }

  return (
    <>
      <div className={classes.aiSuggestionRow}>
        <div className={classes.aiSuggestionRowTop}>
          <WarningCircleIcon height={18} width={18} />
          <OrphanRelationshipName
            classes={classes}
            notFoundText={notFoundText}
            secondLineName={secondLineName}
          />
        </div>
        <div className={classes.aiSuggestionRowBottom}>
          <Typography className={classes.relatedDocRowRelationship} variant="caption">
            {orphanRelType
              ? getRelationshipLabel({ direction: labelDirection, type: orphanRelType })
              : 'Related to'}
          </Typography>
          <div className={classes.aiSuggestionActions}>
            <AiReasoningTooltipButton
              llmReasonsMarkdown={relationship.llmReasonsMarkdown}
              relationshipId={relationship.id}
            />
            <AlineTooltip
              arrow
              placement="top"
              title={`Search repository for ${orphanDirection} document`}
            >
              <span>
                <IconButton
                  disabled={!repositoryId || updating}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setResolutionDialog('select');
                  }}
                >
                  <MagnifyingGlassIcon height={18} width={18} />
                </IconButton>
              </span>
            </AlineTooltip>
            <AlineTooltip arrow placement="top" title={`Upload ${orphanDirection} document`}>
              <span>
                <IconButton
                  disabled={!repositoryId || updating}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setResolutionDialog('upload');
                  }}
                >
                  <UploadNewDocumentIcon height={18} width={18} />
                </IconButton>
              </span>
            </AlineTooltip>
            <AlineTooltip arrow placement="top" title="Reject relationship">
              <span>
                <IconButton
                  disabled={updating}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    void reject(relationship, { documentId });
                  }}
                >
                  <CancelCircleIcon height={18} width={18} />
                </IconButton>
              </span>
            </AlineTooltip>
          </div>
        </div>
      </div>
      {repositoryId && (
        <SelectRelatedDocumentDialog
          excludeDocumentId={documentId}
          open={resolutionDialog === 'select'}
          repositoryId={repositoryId}
          sourceDocumentName={sourceDocumentName ?? 'Document not found'}
          onClose={() => setResolutionDialog(null)}
          onSelect={async selectedDocId => {
            await handleResolve(selectedDocId);
          }}
        />
      )}
      {repositoryId && (
        <UploadFilesPicker
          domContainer={`orphan-editor-upload-${relationship.id}`}
          open={resolutionDialog === 'upload'}
          pickerOpts={{
            afterUploadDone: async res => {
              setResolutionDialog(null);
              const newDocId = res.data?.createFiles?.at(0)?.id ?? null;
              if (!newDocId) return;
              await handleResolve(newDocId);
            },
            maxFiles: 1,
            onClose: () => setResolutionDialog(null),
            props: {
              documentVariant: Apollo.DocumentVariant.Document,
              isSigned: false,
            },
            reportId: repositoryId,
          }}
        />
      )}
    </>
  );
}

/**
 * The extracted name of the document the hint pointed at, stacked above the
 * not-found line. Falls back to the not-found line alone for a hint that never
 * named its target.
 */
function OrphanRelationshipName({
  classes,
  notFoundText,
  secondLineName,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  notFoundText: string;
  secondLineName: string | null;
}): JSX.Element {
  if (!secondLineName)
    return <Typography className={classes.orphanWarningText}>{notFoundText}</Typography>;

  return (
    <div className={classes.orphanNameColumn}>
      <TruncatedTextWithTooltip text={secondLineName} />
      <Typography className={classes.orphanWarningText}>{notFoundText}</Typography>
    </div>
  );
}

/** An AI hint with no document on one side, plus the text describing what it pointed at. */
type UnresolvedRelationship = RelationshipForMutation & {
  expectedDocumentAgreementDate: string | null;
  expectedDocumentName: string | null;
  llmReasonsMarkdown: string | null;
};

function DocumentRelationshipRow({
  classes,
  direction,
  documentId,
  groupLabel,
  isArrivalHighlighted = false,
  isMatchingRunning,
  relatedDocument,
  relationship,
  showDivider,
  variant,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  direction: 'parent' | 'child';
  documentId: UUID;
  isMatchingRunning: boolean;
  relatedDocument: StorableIconDocument & {
    name: string | null;
  };
  relationship: RelationshipForMutation & {
    llmReasonsMarkdown: string | null;
    sourceAgreementName: string | null;
  };
  variant: 'pending' | 'confirmed';
  groupLabel?: string;
  /** True only when this row was just deep-linked into from another document's citation. */
  isArrivalHighlighted?: boolean;
  showDivider?: boolean;
}): JSX.Element {
  const dispatchAlreadyInViewSnack = useDispatchInfoSnack({
    message: 'Document is already in view',
  });
  const rowRef = useArrivalHighlightScroll(isArrivalHighlighted);
  const { classes: arrivalClasses } = useArrivalHighlightStyles();
  const isSelf = relatedDocument.id === documentId;

  /**
   * A self-referencing edge's related document IS the document currently open, so this
   * is an "Internal Reference" — the bound-in agreement's own name (`sourceAgreementName`)
   * takes over the title, and its title click jumps to the bound-in section in place
   * (the container document is already open) instead of navigating anywhere.
   */
  const handleInternalReferenceTitleClick = useRelationshipRowTitleClick({
    documentId,
    llmReasonsMarkdown: relationship.llmReasonsMarkdown,
    relationshipId: relationship.id,
  });
  const internalReferenceTitle = relationship.sourceAgreementName?.trim();
  const displayName = isMatchingRunning
    ? 'Matching document...'
    : isSelf && internalReferenceTitle
      ? internalReferenceTitle
      : relatedDocument.name || 'Untitled';

  const row = (
    <div
      ref={rowRef}
      className={clsx(classes.aiSuggestionRow, !groupLabel && arrivalClasses.rowHighlightTarget)}
      data-arrival-highlighted={isArrivalHighlighted && !groupLabel ? 'true' : undefined}
    >
      <div className={classes.aiSuggestionRowTop}>
        {!isMatchingRunning && (
          <StorableIcon
            iconProps={{ height: RELATED_DOC_ICON_SIZE, width: RELATED_DOC_ICON_SIZE }}
            storable={relatedDocument}
          />
        )}
        <Button
          className={classes.relatedDocRowTitle}
          disabled={isMatchingRunning}
          startIcon={
            isMatchingRunning ? <PulseIcon variant={PulseIconVariant.LoadingPrimary} /> : undefined
          }
          {...(!isSelf && {
            component: Link,
            to: link.DocumentDetail({ documentId: relatedDocument.id }),
          })}
          variant="text"
          onClick={event => {
            if (isSelf) {
              if (!handleInternalReferenceTitleClick()) dispatchAlreadyInViewSnack();
              return;
            }
            openLinkFromAnchorClick(event, link.DocumentDetail({ documentId: relatedDocument.id }));
          }}
        >
          <TruncatedTextWithTooltip text={displayName} />
        </Button>
      </div>
      {isSelf && (
        <div className={classes.selfReferencingCaptionRow}>
          <span aria-hidden className={classes.selfReferencingCaptionSpacer} />
          <Typography className={classes.internalReferenceCaption} variant="caption">
            Internal
          </Typography>
        </div>
      )}
      {(variant === 'confirmed' || !isMatchingRunning) && (
        <div className={classes.aiSuggestionRowBottom}>
          <RelationshipTypeSelectWrapper
            classes={classes}
            direction={direction}
            displayEmpty={variant === 'pending'}
            relationship={relationship}
            showUndoOnChange={variant === 'confirmed'}
          />
          <RelationshipActionButtons
            classes={classes}
            documentId={documentId}
            llmReasonsMarkdown={relationship.llmReasonsMarkdown}
            relationship={relationship}
          />
        </div>
      )}
    </div>
  );

  // Inside a suggestion card the card's own border carries the highlight, not the inner row's.
  if (groupLabel) {
    return (
      <div
        className={clsx(classes.aiSuggestionsGroup, arrivalClasses.cardHighlightTarget)}
        data-arrival-highlighted={isArrivalHighlighted ? 'true' : undefined}
      >
        <Typography className={classes.aiSuggestionsGroupLabel}>{groupLabel}</Typography>
        {row}
      </div>
    );
  }

  return (
    <>
      {row}
      {showDivider && <Divider className={classes.relatedDocRowDivider} />}
    </>
  );
}

function UrlRelationshipTitle({
  childUrl,
  classes,
}: {
  childUrl: string | null | undefined;
  classes: ReturnType<typeof useStyles>['classes'];
}): JSX.Element {
  return (
    <div className={classes.aiSuggestionRowTop}>
      <UrlLinkIcon height={18} width={18} />
      <Button
        className={classes.relatedDocRowTitle}
        variant="text"
        onClick={() => {
          if (childUrl) openLink(childUrl);
        }}
      >
        <AlineTooltip arrow placement="top" title={childUrl ?? ''}>
          <Typography component="span" sx={createClampStyles(2)} variant="inherit">
            {childUrl || 'Untitled'}
          </Typography>
        </AlineTooltip>
      </Button>
    </div>
  );
}

function RelationshipTypeSelectWrapper({
  classes,
  direction,
  displayEmpty,
  relationship,
  showUndoOnChange,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  direction: 'parent' | 'child';
  relationship: RelationshipForMutation;
  displayEmpty?: boolean;
  showUndoOnChange?: boolean;
}): JSX.Element {
  const theme = useTheme();
  const { loading, updateType } = useUpdateRelationshipType();
  const relationshipType = relationship.childRelationshipType ?? null;

  return (
    <RelationshipTypeSelect
      disableUnderline
      className={classes.relationshipSelect}
      disabled={loading}
      displayEmpty={displayEmpty}
      getLabel={type => getRelationshipLabel({ direction, type })}
      menuTitle={`Select the type of ${direction} relation`}
      renderValue={() => {
        if (!relationshipType && displayEmpty) {
          return (
            <span style={{ color: theme.appColors.placeholderType }}>Select relationship</span>
          );
        }
        return getRelationshipLabel({ direction, type: relationshipType });
      }}
      searchPlaceholder="Search for a type..."
      size="small"
      value={relationshipType ?? ''}
      variant="standard"
      onChange={(e: SelectChangeEvent<Apollo.ChildRelationshipType | ''>) => {
        e.stopPropagation();
        const newType = e.target.value;
        if (!newType) return;

        void updateType(
          relationship,
          newType,
          showUndoOnChange ? relationship.childRelationshipType : undefined
        );
      }}
      onClick={e => e.stopPropagation()}
    />
  );
}

function RelationshipActionButtons({
  classes,
  documentId,
  llmReasonsMarkdown,
  relationship,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  documentId: UUID;
  llmReasonsMarkdown: string | null | undefined;
  relationship: RelationshipForMutation;
}): JSX.Element {
  const { loading: rejecting, reject } = useRejectRelationship();
  const { confirm, loading: confirming } = useConfirmRelationship();
  const isConfirmedReview = relationship.review?.outcome === Apollo.ReviewOutcome.Confirmed;
  const isManualRelationship = relationship.source === Apollo.DocumentRelationshipSource.Manual;
  const isMutating = confirming || rejecting;

  if (isConfirmedReview || isManualRelationship) {
    const reviewedByName = relationship.review?.reviewedBy?.displayName ?? null;
    const rejectTooltip = reviewedByName
      ? `Accepted by ${reviewedByName}. Click to reject.`
      : 'Accepted. Click to reject.';

    return (
      <div className={classes.aiSuggestionActions}>
        <AiReasoningTooltipButton
          llmReasonsMarkdown={llmReasonsMarkdown}
          relationshipId={relationship.id}
        />
        <AlineTooltip arrow placement="top" title={rejectTooltip}>
          <span>
            <IconButton
              disabled={isMutating}
              size="small"
              onClick={() => void reject(relationship, { documentId })}
            >
              <CancelCircleIcon height={18} width={18} />
            </IconButton>
          </span>
        </AlineTooltip>
      </div>
    );
  }

  return (
    <div className={classes.aiSuggestionActions}>
      <AiReasoningTooltipButton
        llmReasonsMarkdown={llmReasonsMarkdown}
        relationshipId={relationship.id}
      />
      <AlineTooltip arrow placement="top" title="Reject Relationship">
        <span>
          <IconButton
            disabled={isMutating}
            size="small"
            onClick={() => void reject(relationship, { documentId })}
          >
            <CancelCircleIcon height={18} width={18} />
          </IconButton>
        </span>
      </AlineTooltip>
      <AlineTooltip arrow placement="top" title="Accept Relationship">
        <span>
          <IconButton
            disabled={!relationship.childRelationshipType || isMutating}
            size="small"
            onClick={() => {
              if (!relationship.childRelationshipType) return;
              void confirm(relationship);
            }}
          >
            <CheckCircleIcon height={18} width={18} />
          </IconButton>
        </span>
      </AlineTooltip>
    </div>
  );
}

/** The URL-relationship counterpart of `DocumentRelationshipRow`, with the same grouping rules. */
function UrlRelationshipRow({
  classes,
  documentId,
  groupLabel,
  isArrivalHighlighted = false,
  relationship,
  showDivider,
  variant,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  documentId: UUID;
  relationship: SummaryRelatedDocumentState['confirmedUrlRelationships'][number];
  variant: 'pending' | 'confirmed';
  groupLabel?: string;
  /** True only when this row was just deep-linked into from another document's citation. */
  isArrivalHighlighted?: boolean;
  showDivider?: boolean;
}): JSX.Element {
  const rowRef = useArrivalHighlightScroll(isArrivalHighlighted);
  const { classes: arrivalClasses } = useArrivalHighlightStyles();

  const row = (
    <div
      ref={rowRef}
      className={clsx(classes.aiSuggestionRow, !groupLabel && arrivalClasses.rowHighlightTarget)}
      data-arrival-highlighted={isArrivalHighlighted && !groupLabel ? 'true' : undefined}
    >
      <UrlRelationshipTitle childUrl={relationship.childUrl} classes={classes} />
      <div className={classes.aiSuggestionRowBottom}>
        <RelationshipTypeSelectWrapper
          classes={classes}
          direction="child"
          displayEmpty={variant === 'pending'}
          relationship={relationship}
          showUndoOnChange={variant === 'confirmed'}
        />
        <RelationshipActionButtons
          classes={classes}
          documentId={documentId}
          llmReasonsMarkdown={relationship.llmReasonsMarkdown}
          relationship={relationship}
        />
      </div>
    </div>
  );

  if (groupLabel) {
    return (
      <div
        className={clsx(classes.aiSuggestionsGroup, arrivalClasses.cardHighlightTarget)}
        data-arrival-highlighted={isArrivalHighlighted ? 'true' : undefined}
      >
        <Typography className={classes.aiSuggestionsGroupLabel}>{groupLabel}</Typography>
        {row}
      </div>
    );
  }

  return (
    <>
      {row}
      {showDivider && <Divider className={classes.relatedDocRowDivider} />}
    </>
  );
}

function SuggestedRelationshipRow({
  document,
  documentId,
  highlightedRelationshipId,
  item,
  repositoryId,
}: {
  document: NonNullable<SummaryDocument>;
  documentId: UUID;
  highlightedRelationshipId: UUID | null;
  item: PendingSuggestionItem;
  repositoryId: UUID | null;
}): JSX.Element | null {
  const { classes } = useStyles();

  switch (item.kind) {
    case 'pendingParent': {
      const rel = item.relationship;
      if (!rel.parentDocument) return null;
      return (
        <DocumentRelationshipRow
          classes={classes}
          direction="parent"
          documentId={documentId}
          groupLabel="Parent"
          isArrivalHighlighted={rel.id === highlightedRelationshipId}
          isMatchingRunning={getIsRelationshipLookingForAMatch(document, rel)}
          relatedDocument={rel.parentDocument}
          relationship={rel}
          variant="pending"
        />
      );
    }
    case 'pendingChild': {
      const rel = item.relationship;
      if (!rel.childDocument) return null;
      return (
        <DocumentRelationshipRow
          classes={classes}
          direction="child"
          documentId={documentId}
          groupLabel="Child"
          isArrivalHighlighted={rel.id === highlightedRelationshipId}
          isMatchingRunning={getIsRelationshipLookingForAMatch(document, rel)}
          relatedDocument={rel.childDocument}
          relationship={rel}
          variant="pending"
        />
      );
    }
    case 'pendingUrl': {
      const rel = item.relationship;
      return (
        <UrlRelationshipRow
          classes={classes}
          documentId={documentId}
          groupLabel="Child URL"
          isArrivalHighlighted={rel.id === highlightedRelationshipId}
          relationship={rel}
          variant="pending"
        />
      );
    }
    case 'orphanParent': {
      const rel = item.relationship;
      return (
        <OrphanRelationshipCard
          classes={classes}
          documentId={documentId}
          groupLabel="Parent"
          isArrivalHighlighted={rel.id === highlightedRelationshipId}
          orphanDirection="parent"
          relationship={rel}
          repositoryId={repositoryId}
          sourceDocumentName={document.name ?? undefined}
        />
      );
    }
    case 'orphanChild': {
      const rel = item.relationship;
      return (
        <OrphanRelationshipCard
          classes={classes}
          documentId={documentId}
          groupLabel="Child"
          isArrivalHighlighted={rel.id === highlightedRelationshipId}
          orphanDirection="child"
          relationship={rel}
          repositoryId={repositoryId}
          sourceDocumentName={document.name ?? undefined}
        />
      );
    }
  }
}

/**
 * The card around an unresolved AI hint. It carries the arrival highlight itself — the row inside
 * it has no document to link to, so the card is the only thing a deep link can land on.
 */
function OrphanRelationshipCard({
  classes,
  documentId,
  groupLabel,
  isArrivalHighlighted,
  orphanDirection,
  relationship,
  repositoryId,
  sourceDocumentName,
}: {
  classes: ReturnType<typeof useStyles>['classes'];
  documentId: UUID;
  groupLabel: string;
  isArrivalHighlighted: boolean;
  orphanDirection: 'parent' | 'child';
  relationship: UnresolvedRelationship;
  repositoryId: UUID | null;
  sourceDocumentName: string | undefined;
}): JSX.Element {
  const { classes: arrivalClasses } = useArrivalHighlightStyles();
  const cardRef = useArrivalHighlightScroll(isArrivalHighlighted);

  return (
    <div
      ref={cardRef}
      className={clsx(classes.aiSuggestionsGroup, arrivalClasses.cardHighlightTarget)}
      data-arrival-highlighted={isArrivalHighlighted ? 'true' : undefined}
    >
      <Typography className={classes.aiSuggestionsGroupLabel}>{groupLabel}</Typography>
      <OrphanRelationshipEditorRow
        classes={classes}
        documentId={documentId}
        orphanDirection={orphanDirection}
        relationship={relationship}
        repositoryId={repositoryId}
        sourceDocumentName={sourceDocumentName}
      />
    </div>
  );
}

function RelatedDocuments({
  document,
  documentId,
  expanded: isExpanded,
  loading,
  onAiPendingSuggestionsCountChange,
  onBulkReviewComplete,
  onExpandedChange,
}: {
  document: SummaryDocument;
  documentId: UUID;
  expanded: boolean;
  loading: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onAiPendingSuggestionsCountChange?: (count: number) => void;
  onBulkReviewComplete?: () => Promise<void> | void;
}): JSX.Element {
  const { classes } = useStyles();
  const currentUser = useSelectCurrentUser();
  const [dialogOpen, setDialogOpen] = useState(false);
  const state = useSummaryRelatedDocumentState(document);
  const repositoryId = currentUser?.myRepositoryId ?? null;
  const aiBulkReview = getAiBulkReviewPayload(state.aiPendingItems);
  const [bulkReject] = Apollo.useBulkRejectDocumentRelationshipsMutation();
  const [bulkConfirm] = Apollo.useBulkConfirmDocumentRelationshipsMutation();
  const reviewedBy = currentUser
    ? { __typename: 'User' as const, displayName: currentUser.displayName, id: currentUser.id }
    : null;
  const isRelationshipMatchingRunning = document
    ? getIsRelationshipMatchingRunning(document)
    : false;
  const [direction, setDirection] = useState<RelationshipDirection>('parent');
  const highlightedRelationshipId = useArrivalHighlightedRelationshipId();

  useEffect(() => {
    onAiPendingSuggestionsCountChange?.(state.aiPendingItems.length);
  }, [onAiPendingSuggestionsCountChange, state.aiPendingItems.length]);

  function buildOptimisticRelationship(
    rel: {
      childRelationshipType: Apollo.ChildRelationshipType;
      id: string;
      parentDocumentId: string | null;
      source: Apollo.DocumentRelationshipSource;
      childDocumentId?: string | null;
    },
    outcome: Apollo.ReviewOutcome
  ) {
    return {
      __typename: 'DocumentRelationship' as const,
      childDocumentId: rel.childDocumentId ?? null,
      childRelationshipType: rel.childRelationshipType,
      id: rel.id,
      parentDocumentId: rel.parentDocumentId ?? null,
      review: reviewedBy
        ? {
            __typename: 'DocumentRelationshipReview' as const,
            outcome,
            reviewedBy,
          }
        : null,
      source: rel.source,
    };
  }

  async function handleAcceptAll(): Promise<void> {
    const { docRels, targets, urlRels } = aiBulkReview;
    if (!targets.length) return;

    try {
      await bulkConfirm({
        optimisticResponse: {
          __typename: 'Mutation' as const,
          bulkConfirmDocumentRelationships: {
            __typename: 'BulkReviewRelationshipsResponse' as const,
            skippedCount: 0,
            updatedRelationships: [
              ...docRels.map(rel =>
                buildOptimisticRelationship(rel, Apollo.ReviewOutcome.Confirmed)
              ),
              ...urlRels.map(rel =>
                buildOptimisticRelationship(rel, Apollo.ReviewOutcome.Confirmed)
              ),
            ],
          },
        },
        variables: { input: { targets } },
      });
    } finally {
      await onBulkReviewComplete?.();
    }
  }

  async function handleRejectAll(): Promise<void> {
    const { docRels, targets, urlRels } = aiBulkReview;
    if (!targets.length) return;

    try {
      await bulkReject({
        optimisticResponse: {
          __typename: 'Mutation' as const,
          bulkRejectDocumentRelationships: {
            __typename: 'BulkReviewRelationshipsResponse' as const,
            skippedCount: 0,
            updatedRelationships: [
              ...docRels.map(rel =>
                buildOptimisticRelationship(rel, Apollo.ReviewOutcome.Rejected)
              ),
              ...urlRels.map(rel =>
                buildOptimisticRelationship(rel, Apollo.ReviewOutcome.Rejected)
              ),
            ],
          },
        },
        variables: { input: { targets } },
      });
    } finally {
      await onBulkReviewComplete?.();
    }
  }

  if (loading || !document) {
    return (
      <Section
        headerChildren={
          <>
            <Spacer />
            <Skeleton height={20} variant="rectangular" width={24} />
          </>
        }
        title="Related Documents"
      >
        <Skeleton height={90} variant="rectangular" />
      </Section>
    );
  }

  return (
    <>
      <Section
        headerChildren={
          <>
            {(state.relatedCount > 0 || state.aiPendingItems.length === 0) && (
              <Chip className={classes.relatedCountChip} label={state.relatedCount} size="small" />
            )}
            <Spacer />
            <AlineTooltip arrow placement="top" title={isExpanded ? 'Collapse' : 'Expand'}>
              <IconButton size="small" onClick={() => onExpandedChange(!isExpanded)}>
                <AnimatedArrowIcon rotated={isExpanded} />
              </IconButton>
            </AlineTooltip>
          </>
        }
        headerId={domElIdCreators.documentSummaryRelatedDocumentsHeader(documentId)}
        title="Related Documents"
      >
        <Collapse in={isExpanded}>
          {state.aiPendingItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                mb: 1,
              }}
            >
              <div className={classes.aiSuggestionsHeaderRow}>
                <div className={classes.aiSuggestionsHeaderLeft}>
                  <SmallAiSparkleIcon />
                  <Typography className={classes.aiSuggestionsHeaderText} variant="body2">
                    AI Suggestions
                  </Typography>
                </div>
                {!isRelationshipMatchingRunning && aiBulkReview.targets.length > 0 && (
                  <div className={classes.aiSuggestionsHeaderActions}>
                    <Button
                      className={classes.aiSuggestionsHeaderActionButton}
                      size="small"
                      startIcon={
                        <CancelCircleIcon fill="rgba(55, 73, 81, 1)" height={14} width={14} />
                      }
                      variant="text"
                      onClick={e => {
                        e.stopPropagation();
                        void handleRejectAll();
                      }}
                    >
                      Reject all
                    </Button>
                    <Button
                      className={classes.aiSuggestionsHeaderActionButton}
                      size="small"
                      startIcon={
                        <CheckCircleIcon fill="rgba(55, 73, 81, 1)" height={14} width={14} />
                      }
                      variant="text"
                      onClick={e => {
                        e.stopPropagation();
                        void handleAcceptAll();
                      }}
                    >
                      Accept all
                    </Button>
                  </div>
                )}
              </div>

              {state.aiPendingItems.map(item => (
                <SuggestedRelationshipRow
                  key={item.relationship.id}
                  document={document}
                  documentId={documentId}
                  highlightedRelationshipId={highlightedRelationshipId}
                  item={item}
                  repositoryId={repositoryId}
                />
              ))}
            </Box>
          )}

          <div className={classes.relatedDocsGroup}>
            <div className={classes.relatedDocsGroupTitleRow}>
              <FavoriteDocumentIcon />
              <Typography className={classes.relatedDocsGroupTitleText} variant="body2">
                Parent Documents
              </Typography>
              <Spacer />
              <IconButton
                disabled={!repositoryId}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setDirection('parent');
                  setDialogOpen(true);
                }}
              >
                <AddIcon />
              </IconButton>
            </div>
            {state.confirmedParentRelationships.length ? (
              <Box className={classes.relatedDocsList}>
                {state.confirmedParentRelationships.map((rel, index) => {
                  if (!rel.parentDocument) return null;
                  return (
                    <DocumentRelationshipRow
                      key={rel.id}
                      classes={classes}
                      direction="parent"
                      documentId={documentId}
                      isArrivalHighlighted={rel.id === highlightedRelationshipId}
                      isMatchingRunning={getIsRelationshipLookingForAMatch(document, rel)}
                      relatedDocument={rel.parentDocument}
                      relationship={rel}
                      showDivider={index < state.confirmedParentRelationships.length - 1}
                      variant="confirmed"
                    />
                  );
                })}
              </Box>
            ) : (
              <div className={classes.relatedDocsEmptyState}>
                <Typography className={classes.relatedDocsEmptyText} variant="body2">
                  {state.pendingParentCount > 0
                    ? 'No confirmed documents. Review the suggestions above.'
                    : 'No documents identified'}
                </Typography>
                <Button
                  className={classes.relatedDocsAddButton}
                  disabled={!repositoryId}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDirection('parent');
                    setDialogOpen(true);
                  }}
                >
                  Add new relationship
                </Button>
              </div>
            )}
          </div>

          <div className={classes.relatedDocsGroup}>
            <div className={classes.relatedDocsGroupTitleRow}>
              <ChildDocumentIcon />
              <Typography className={classes.relatedDocsGroupTitleText} variant="body2">
                Child Documents
              </Typography>
              <Spacer />
              <IconButton
                disabled={!repositoryId}
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setDirection('child');
                  setDialogOpen(true);
                }}
              >
                <AddIcon />
              </IconButton>
            </div>
            {state.confirmedChildRelationships.length || state.confirmedUrlRelationships.length ? (
              <Box className={classes.relatedDocsList}>
                {state.confirmedChildRelationships.map((rel, index) => {
                  if (!rel.childDocument) return null;

                  return (
                    <DocumentRelationshipRow
                      key={rel.id}
                      classes={classes}
                      direction="child"
                      documentId={documentId}
                      isArrivalHighlighted={rel.id === highlightedRelationshipId}
                      isMatchingRunning={getIsRelationshipLookingForAMatch(document, rel)}
                      relatedDocument={rel.childDocument}
                      relationship={rel}
                      showDivider={
                        index < state.confirmedChildRelationships.length - 1 ||
                        state.confirmedUrlRelationships.length > 0
                      }
                      variant="confirmed"
                    />
                  );
                })}
                {state.confirmedUrlRelationships.map((rel, index) => (
                  <UrlRelationshipRow
                    key={rel.id}
                    classes={classes}
                    documentId={documentId}
                    isArrivalHighlighted={rel.id === highlightedRelationshipId}
                    relationship={rel}
                    showDivider={index < state.confirmedUrlRelationships.length - 1}
                    variant="confirmed"
                  />
                ))}
              </Box>
            ) : (
              <div className={classes.relatedDocsEmptyState}>
                <Typography className={classes.relatedDocsEmptyText} variant="body2">
                  {state.pendingChildCount > 0
                    ? 'No confirmed documents. Review the suggestions above.'
                    : 'No documents identified'}
                </Typography>
                <Button
                  className={classes.relatedDocsAddButton}
                  disabled={!repositoryId}
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setDirection('child');
                    setDialogOpen(true);
                  }}
                >
                  Add new relationship
                </Button>
              </div>
            )}
          </div>
        </Collapse>
      </Section>
      {repositoryId && (
        <SelectRelatedDocumentsWithRelationshipDialog
          direction={direction}
          documentId={documentId}
          open={dialogOpen}
          repositoryId={repositoryId}
          sourceDocumentName={document?.name ?? undefined}
          onClose={() => setDialogOpen(false)}
        />
      )}
    </>
  );
}

export type AIQuestion = ReturnType<
  typeof useGetExtractionQuestionsForDocument
>['extractionQuestions'][number];

type DocumentVersionSubset = Pick<
  Apollo.DocumentVersion,
  'createdAt' | 'fileContentChangedAt' | 'id' | '__typename'
>;
type StaticFileVersionSubset = Pick<
  Apollo.StaticFileVersion,
  | 'createdAt'
  | 'fileContentChangedAt'
  | 'fileExtractionFailedAt'
  | 'fileExtractionFinishedAt'
  | 'fileExtractionStartedAt'
  | 'id'
  | '__typename'
>;
export interface DocumentWithAiQuestionIdsQueueing extends Pick<
  Apollo.Document,
  | 'aiQuestionIdsQueueing'
  | 'coreAiPropertiesExtractionFailedAt'
  | 'coreAiPropertiesExtractionFinishedAt'
  | 'coreAiPropertiesExtractionStartedAt'
  | 'id'
  | '__typename'
> {
  latestVersion: DocumentVersionSubset | StaticFileVersionSubset;
}

function AIProperties({ documentId }: { documentId: UUID }): JSX.Element {
  const { classes } = useStyles();
  const [questionIdArrival, acknowledgeQuestionIdArrival] = useConsumePendingParam(
    params.questionId
  );
  const [deepLinkQuestionId, setDeepLinkQuestionId] = useState<UUID | null>(null);
  const [highlightedQuestionId, setHighlightedQuestionId] = useState<UUID | null>(null);
  const editor = useMaybeEditor();
  const editorLoading = useEditorLoading();
  const documentViewer = useMaybeDocumentViewer();
  const clearHighlightRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const questionIdScrolledToInDocRef = useRef<UUID | null>(null);
  const { scrollToLocationForReference } = useScrollToLocationForReference();
  const {
    document: alineDocument,
    extractionQuestions,
    loading,
  } = useGetExtractionQuestionsForDocument(documentId);

  useEffect(() => {
    if (loading || !alineDocument || !questionIdArrival) return;
    if (!extractionQuestions.some(question => question.id === questionIdArrival.value)) return;

    acknowledgeQuestionIdArrival();
    setDeepLinkQuestionId(questionIdArrival.value);
  }, [
    acknowledgeQuestionIdArrival,
    alineDocument,
    questionIdArrival,
    extractionQuestions,
    loading,
  ]);

  const handleQuestionDeepLinkFound = useCallback((questionId: UUID) => {
    setHighlightedQuestionId(questionId);
    setDeepLinkQuestionId(null);
    questionIdScrolledToInDocRef.current = null;

    if (clearHighlightRef.current) clearTimeout(clearHighlightRef.current);
    clearHighlightRef.current = setTimeout(() => setHighlightedQuestionId(null), 7500);
  }, []);

  useEffect(() => {
    return () => {
      if (clearHighlightRef.current) clearTimeout(clearHighlightRef.current);
    };
  }, []);

  /**
   * Once the editor or document viewer is ready, scroll the document canvas to
   * the first citation of the highlighted question. This runs as a follow-up
   * to the effect above because the Slate editor instance is created
   * immediately but its YJS shared-root content loads asynchronously; we wait
   * on `editorLoading` so Node.has sees the populated document.
   */
  useEffect(() => {
    if (!highlightedQuestionId) return;
    if (questionIdScrolledToInDocRef.current === highlightedQuestionId) return;

    const question = extractionQuestions.find(q => q.id === highlightedQuestionId);
    const firstLocation = sortExtractionLocations(question?.extractionJob?.locations).at(0);
    if (!firstLocation) return;

    if (firstLocation.__typename === 'FileLocation' && !documentViewer) return;
    if (firstLocation.__typename === 'AlineLocation' && (!editor || editorLoading)) return;

    scrollToLocationForReference({ aiReference: firstLocation });
    questionIdScrolledToInDocRef.current = highlightedQuestionId;
  }, [
    highlightedQuestionId,
    extractionQuestions,
    editor,
    editorLoading,
    documentViewer,
    scrollToLocationForReference,
  ]);

  return (
    <>
      <AlineAIProperties
        deepLinkQuestionId={deepLinkQuestionId}
        document={alineDocument}
        highlightedQuestionId={highlightedQuestionId}
        loading={loading}
        standardQuestions={extractionQuestions.filter(
          q => q.variant !== Apollo.ExtractionQuestionVariant.Custom
        )}
        onDeepLinkQuestionFound={handleQuestionDeepLinkFound}
      />
      <Divider className={classes.divider} />
      <CustomAIProperties
        customQuestions={extractionQuestions.filter(
          q => q.variant === Apollo.ExtractionQuestionVariant.Custom
        )}
        deepLinkQuestionId={deepLinkQuestionId}
        document={alineDocument}
        highlightedQuestionId={highlightedQuestionId}
        loading={loading}
        onDeepLinkQuestionFound={handleQuestionDeepLinkFound}
      />
    </>
  );
}

function StandardPropertiesContent({
  deepLinkQuestionId,
  document,
  highlightedQuestionId,
  onDeepLinkQuestionFound,
  standardQuestions,
}: {
  deepLinkQuestionId: UUID | null;
  document: DocumentWithAiQuestionIdsQueueing;
  highlightedQuestionId: UUID | null;
  onDeepLinkQuestionFound: (questionId: UUID) => void;
  standardQuestions: AIQuestion[];
}): JSX.Element {
  const { classes } = useStyles();
  const sortedStandardQuestions = [...standardQuestions].sort((a, b) =>
    a.title.localeCompare(b.title)
  );

  if (!sortedStandardQuestions.length)
    return (
      <div className={classes.noQuestions}>
        <Typography align="center" variant="subtitle2">
          No core AI properties found.
        </Typography>
      </div>
    );

  return (
    <>
      {sortedStandardQuestions.map(question => {
        return (
          <AIQuestionContainer
            key={question.id}
            deepLinkQuestionId={deepLinkQuestionId}
            highlightedQuestionId={highlightedQuestionId}
            questionId={question.id}
            onDeepLinkQuestionFound={onDeepLinkQuestionFound}
          >
            <ExtractionQuestionCard
              document={document}
              // Standard questions are not editable.
              editingTuple={[false, doNothing]}
              hideEditButton={true}
              hideQuestionText={true}
              question={question}
              onClose={doNothing}
            />
          </AIQuestionContainer>
        );
      })}
    </>
  );
}

function AIQuestionContainer({
  children,
  deepLinkQuestionId,
  editing = false,
  highlightedQuestionId,
  onDeepLinkQuestionFound,
  questionId,
}: {
  children: React.ReactNode;
  deepLinkQuestionId: UUID | null;
  highlightedQuestionId: UUID | null;
  onDeepLinkQuestionFound: (questionId: UUID) => void;
  questionId: UUID;
  editing?: boolean;
}): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const ref = useRef<HTMLDivElement | null>(null);
  const isHighlighted = highlightedQuestionId === questionId;

  useEffect(() => {
    if (deepLinkQuestionId !== questionId) return;

    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    onDeepLinkQuestionFound(questionId);
  }, [deepLinkQuestionId, onDeepLinkQuestionFound, questionId]);

  return (
    <div
      ref={ref}
      className={classes.questionContainer}
      id={domElIdCreators.questionEl(questionId)}
      style={{
        border: editing || isHighlighted ? `1px solid ${theme.palette.primary.main}` : undefined,
      }}
    >
      {children}
    </div>
  );
}

function AlineAIProperties({
  deepLinkQuestionId,
  document,
  highlightedQuestionId,
  loading,
  onDeepLinkQuestionFound,
  standardQuestions,
}: {
  deepLinkQuestionId: UUID | null;
  document: DocumentWithAiQuestionIdsQueueing | null;
  highlightedQuestionId: UUID | null;
  loading: boolean;
  onDeepLinkQuestionFound: (questionId: UUID) => void;
  standardQuestions: AIQuestion[];
}): JSX.Element {
  const { classes } = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);
  const { cancelExtractionJobs } = useCancelExtractionJobs();
  const { startExtractionJobsForDocuments } = useRunExtractionJobsForDocuments();
  const hasRunningQuestions = standardQuestions.some(q =>
    getIsExtractionQuestionRunning(document, q)
  );

  const handleRunAll = async (e: React.MouseEvent) => {
    e.stopPropagation();

    if (!standardQuestions.length || !document) return;

    await startExtractionJobsForDocuments({
      variables: {
        input: {
          documentIds: [document.id],
          questionIds: standardQuestions.map(q => q.id),
        },
      },
    });
  };

  const handleStopAll = async (e: React.MouseEvent) => {
    e.stopPropagation();

    const questionsWithRunningJob = standardQuestions.flatMap(q => {
      const isStandardQuestionWithRunningJob =
        q.variant !== Apollo.ExtractionQuestionVariant.Custom &&
        getIsExtractionJobRunning(q.extractionJob);
      if (!isStandardQuestionWithRunningJob) return [];

      return q.extractionJob ? [{ ...q, extractionJob: q.extractionJob }] : [];
    });
    if (!questionsWithRunningJob.length || !document) return;

    await cancelExtractionJobs(document.id, {
      questionsWithJobList: questionsWithRunningJob,
    });
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  if (loading || !document) {
    return (
      <div className={classes.section}>
        <div className={classes.sectionHeader}>
          <Typography variant="subtitle1">Aline AI Standard Properties</Typography>
          <Spacer />
          <Skeleton height={32} variant="circular" width={32} />
        </div>
        <div className={classes.sectionContent}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} height={80} variant="rectangular" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <Section
      headerChildren={
        <>
          <Spacer />
          {hasRunningQuestions && (
            <AlineTooltip arrow placement="top" title="Stop all">
              <IconButton color="primary" size="small" onClick={handleStopAll}>
                <StopIcon />
              </IconButton>
            </AlineTooltip>
          )}
          <AlineTooltip arrow placement="top" title="Run all">
            <IconButton
              color="primary"
              disabled={!standardQuestions.length}
              size="small"
              onClick={handleRunAll}
            >
              <PlayWorkflowIcon />
            </IconButton>
          </AlineTooltip>
          <AlineTooltip arrow placement="top" title={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton size="small" onClick={toggleExpanded}>
              <AnimatedArrowIcon rotated={isExpanded} />
            </IconButton>
          </AlineTooltip>
        </>
      }
      title="Aline AI Standard Properties"
    >
      <Collapse in={isExpanded}>
        <div className={classes.sectionContent}>
          <StandardPropertiesContent
            deepLinkQuestionId={deepLinkQuestionId}
            document={document}
            highlightedQuestionId={highlightedQuestionId}
            standardQuestions={standardQuestions}
            onDeepLinkQuestionFound={onDeepLinkQuestionFound}
          />
        </div>
      </Collapse>
    </Section>
  );
}

function CustomPropertiesContent({
  customQuestions,
  deepLinkQuestionId,
  document,
  editingQuestionIdTuple: [editingQuestionId, setEditingQuestionId],
  highlightedQuestionId,
  onDeepLinkQuestionFound,
}: {
  customQuestions: ReturnType<typeof useGetExtractionQuestionsForDocument>['extractionQuestions'];
  deepLinkQuestionId: UUID | null;
  document: DocumentWithAiQuestionIdsQueueing;
  editingQuestionIdTuple: ReactSetStateTuple<UUID | null>;
  highlightedQuestionId: UUID | null;
  onDeepLinkQuestionFound: (questionId: UUID) => void;
}): JSX.Element {
  const { classes } = useStyles();

  if (!customQuestions.length)
    return (
      <div className={classes.noQuestions}>
        <Typography align="center" variant="subtitle2">
          No custom AI properties defined yet.
        </Typography>
      </div>
    );

  return (
    <>
      {customQuestions.map(question => {
        const editing = editingQuestionId === question.id;
        const setEditing = () => setEditingQuestionId(question.id);

        return (
          <AIQuestionContainer
            key={question.id}
            deepLinkQuestionId={deepLinkQuestionId}
            editing={editing}
            highlightedQuestionId={highlightedQuestionId}
            questionId={question.id}
            onDeepLinkQuestionFound={onDeepLinkQuestionFound}
          >
            <ExtractionQuestionCard
              document={document}
              editingTuple={[editing, setEditing]}
              hideEditButton={false}
              hideQuestionText={false}
              question={question}
              onClose={() => setEditingQuestionId(null)}
            />
          </AIQuestionContainer>
        );
      })}
    </>
  );
}

function CustomAIProperties({
  customQuestions,
  deepLinkQuestionId,
  document,
  highlightedQuestionId,
  loading,
  onDeepLinkQuestionFound,
}: {
  customQuestions: AIQuestion[];
  deepLinkQuestionId: UUID | null;
  document: DocumentWithAiQuestionIdsQueueing | null;
  highlightedQuestionId: UUID | null;
  loading: boolean;
  onDeepLinkQuestionFound: (questionId: UUID) => void;
}): JSX.Element {
  const { classes } = useStyles();
  const [isExpanded, setIsExpanded] = useState(true);
  const { createExtractionQuestion } = useCreateExtractionQuestion();
  const [editingQuestionId, setEditingQuestionId] = useState<UUID | null>(null);

  if (loading || !document) {
    return (
      <Section title="Custom AI Properties">
        <div className={classes.sectionHeader}>
          <Typography variant="subtitle1">Custom AI Properties</Typography>
          <Spacer />
          <Skeleton height={32} variant="circular" width={32} />
        </div>
        <div className={classes.sectionContent}>
          {Array.from({ length: 3 }).map((_, index) => (
            <Skeleton key={index} height={80} variant="rectangular" />
          ))}
        </div>
      </Section>
    );
  }

  const handleAddQuestion = async (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();

    const questionText = '';
    const title = 'Untitled';
    const category = 'Custom';
    const type = Apollo.ExtractionQuestionType.Text;
    const { data } = await createExtractionQuestion({
      optimisticResponse: {
        __typename: 'Mutation',
        createExtractionQuestion: {
          __typename: 'ExtractionQuestion',
          aiGenerated: false,
          aiGuidance: null,
          category,
          contentHash: uuid4(),
          contractTypes: [],
          createdAt: new Date().toISOString(),
          id: uuid4(),
          inputFileFields: [],
          inputs: [],
          isActive: true,
          mode: Apollo.ExtractionQuestionMode.Ai,
          options: [],
          questionText,
          title,
          type,
          updatedAt: new Date().toISOString(),
          variant: Apollo.ExtractionQuestionVariant.Custom,
        },
      },
      variables: {
        input: {
          category,
          questionText,
          title,
          type,
        },
      },
    });
    if (data?.createExtractionQuestion) setEditingQuestionId(data.createExtractionQuestion.id);
  };

  const sortedCustomQuestions = [...customQuestions].sort(
    getDateComparator({ dateKey: DateKey.createdAt, direction: Direction.Descending })
  );

  return (
    <Section
      headerChildren={
        <>
          <Spacer />
          <AlineTooltip arrow placement="top" title="Add Question">
            <div>
              <IconButton size="small" onClick={handleAddQuestion}>
                <AddIcon />
              </IconButton>
            </div>
          </AlineTooltip>
          <AlineTooltip arrow placement="top" title={isExpanded ? 'Collapse' : 'Expand'}>
            <IconButton size="small" onClick={() => setIsExpanded(!isExpanded)}>
              <AnimatedArrowIcon rotated={isExpanded} />
            </IconButton>
          </AlineTooltip>
        </>
      }
      title="Custom AI Properties"
    >
      <Collapse in={isExpanded}>
        <div className={classes.sectionContent}>
          <CustomPropertiesContent
            customQuestions={sortedCustomQuestions}
            deepLinkQuestionId={deepLinkQuestionId}
            document={document}
            editingQuestionIdTuple={[editingQuestionId, setEditingQuestionId]}
            highlightedQuestionId={highlightedQuestionId}
            onDeepLinkQuestionFound={onDeepLinkQuestionFound}
          />
        </div>
      </Collapse>
    </Section>
  );
}
